import { AxiosResponse } from 'axios';
import { FBaseService } from '@kasasa/fbase-components/lib';
import { Cycle } from '@/api/cycles/Cycle';
import { QualOverride } from './QualOverride';
import { QualificationType } from './QualificationType';

interface QualOverrideCreator {
  allProducts: boolean,
  externalProductMappingId: string,
  startDate: string,
  endDate: string | null,
  qualRewardLevel: number,
  qualOverrideType: string,
  coreAccountNumbersText: string,
  overrideForAllAccounts: boolean,
  overrideForAllFuture: boolean | null
}

export class QualOverrideResource extends FBaseService {
  public async getOverrides(fiId: string): Promise<QualOverride[]> {
    const response: AxiosResponse = await this.apiClient.get(
      `/api/rcx/config/${fiId}/qual-override/`,
    );
    const results: QualOverride[] = response.data.data;
    return results;
  }

  public async deleteQualOverrideByAccounts(fiId: string, akks: string[], cycleStartDate: Date): Promise<string[]> {
    const response: AxiosResponse = await this.apiClient.put(
      `/api/rcx/config/${fiId}/qual-override/by-account/${cycleStartDate.toISOString().substr(0, 10)}`, akks,
    );
    const results: string[] = response.data.data;
    return results;
  }

  public async deleteQualOverrideById(fiId: string, overrideId: string): Promise<AxiosResponse> {
    return this.apiClient.delete(`/api/rcx/config/${fiId}/qual-override/${overrideId}`);
  }

  public async createQualOverrideForAccount(fiId: string, coreAccountNumber: string, externalProductMappingId: string,
    cycle: Cycle, forAllFuture: boolean | null, rewardLevel: number): Promise<QualOverrideCreator> {
    const qualOverrideCreator: QualOverrideCreator = QualOverrideResource.createQualOverrideCreatorForAccount(
      externalProductMappingId, coreAccountNumber, cycle, forAllFuture, rewardLevel,
    );

    const response: AxiosResponse = await this.apiClient.put(
      `/api/rcx/reporting/${fiId}/qual-override-creator`, qualOverrideCreator,
    );
    const result: QualOverrideCreator = response.data.data;
    return result;
  }

  private static createQualOverrideCreatorForAccount(externalProductMappingId: string, coreAccountNumber: string,
    cycle: Cycle, forAllFuture: boolean | null, rewardLevel: number): QualOverrideCreator {
    return {
      allProducts: false,
      externalProductMappingId,
      coreAccountNumbersText: coreAccountNumber,
      qualOverrideType: 'OVERALL',
      startDate: new Date(cycle.qualificationStartDate).toISOString().substr(0, 10),
      overrideForAllFuture: forAllFuture,
      endDate: forAllFuture ? null : new Date(cycle.qualificationEndDate).toISOString().substr(0, 10),
      qualRewardLevel: rewardLevel,
      overrideForAllAccounts: false,
    };
  }

  public async createQualOverride(fiId: string, cycle: Cycle, externalProductMappingId: number | null,
    allProducts: boolean, allAccounts: boolean | null, accountNumberText: string,
    overallQualification: boolean | null, qualType: QualificationType | null, level: number): Promise<QualOverrideCreator> {
    const qualOverrideCreator: QualOverrideCreator = QualOverrideResource.createQualOverrideCreator(
      cycle, externalProductMappingId, allProducts, allAccounts, accountNumberText,
      overallQualification, qualType, level,
    );

    const response: AxiosResponse = await this.apiClient.put(
      `/api/rcx/reporting/${fiId}/qual-override-creator`, qualOverrideCreator,
    );
    const result: QualOverrideCreator = response.data.data;
    return result;
  }

  private static createQualOverrideCreator(cycle: Cycle, externalProductMappingId: number | null,
    allProducts: boolean, allAccounts: boolean | null, accountNumberText: string,
    overallQualification: boolean | null, qualType: QualificationType | null, level: number): QualOverrideCreator {
    const qualTypeString: string = qualType ? qualType.qualifierType : '';

    return {
      allProducts,
      externalProductMappingId: `${externalProductMappingId}`,
      coreAccountNumbersText: accountNumberText.replace(/\n/g, ' '),
      qualOverrideType: overallQualification ? 'OVERALL' : qualTypeString,
      startDate: new Date(cycle.qualificationStartDate).toISOString().substr(0, 10),
      endDate: new Date(cycle.qualificationEndDate).toISOString().substr(0, 10),
      overrideForAllFuture: false,
      qualRewardLevel: level,
      overrideForAllAccounts: !!allAccounts,
    };
  }
}

export default QualOverrideResource;
